import { Link } from "@remix-run/react";
import type { IMedia } from "~/types";
import type { IThematicFoci } from "~/api/get_themenschwerpunkte_data.server";
import { classNames, getImageUrl, toSafePathSegment } from "~/utils";
import theme from "~/theme/default";
import { ListItem } from "~/shared";
import { UnifiedCardHome } from "~/components";
import dotsBackground from "~/assets/images/inkusion/bg-dot-pattern-lightgray.png";

/* ------------------------------------------------------------------ */
/*  Main export                                                        */
/* ------------------------------------------------------------------ */
export function MediaHome({
  thematicFoci,
  mediaData,
  mediaCount,
}: {
  thematicFoci: IThematicFoci[];
  mediaData: IMedia[];
  mediaCount?: number;
}) {
  const { button } = theme;
  const { base } = button;

  const portfolioItems = thematicFoci.slice(0, 3);
  const mediaItems = mediaData.slice(0, 9);

  const articleLink = (id: number, name: string, slug?: string) =>
    `/themenschwerpunkte/${id}/${toSafePathSegment(slug || name)}`;

  const mediaLink = (id: number, name: string, slug?: string) =>
    `/search/main/${id}/${toSafePathSegment(slug || name)}`;

  return (
    <div
      className="relative"
      style={{
        backgroundImage: `url(${dotsBackground})`,
        backgroundSize: "80%",
        backgroundPosition: "100% 0%",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="section-wrapper">
        {/* ===== Medienportfolios ===== */}
        <section>
          <div className="mt-6 flex flex-col justify-between md:gap-6 lg:flex-row lg:items-start lg:gap-10">
            {/* Title + link + 3 portfolio cards */}
            <div className="md:min-w-0 md:flex-1">
              <div className="flex flex-col items-baseline justify-between pb-8 lg:flex-row">
                <h2 className="text-2xl font-bold text-textColor-primary lg:text-[40px]">
                  Medienportfolios
                </h2>
                <Link
                  to="/themenschwerpunkte"
                  className="flex items-center gap-1 text-sm font-medium text-textColor-primary underline hover:text-primary"
                >
                  weitere laden
                  <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
              <div className="flex flex-col justify-center gap-3 md:items-start lg:flex-row lg:justify-start">
                {portfolioItems.map((item) => (
                  <ListItem
                    key={item.id}
                    to={articleLink(item.id, item.name, item.slug)}
                    className="w-full sm:w-auto md:w-[60%] md:max-w-none xl:w-full"
                  >
                    <UnifiedCardHome
                      name={item.name}
                      imageUrl={
                        item.picture ? getImageUrl(item.picture.file.url) : ""
                      }
                      layout="vertical"
                    />
                  </ListItem>
                ))}
              </div>
            </div>

            {/* Right column: Betterplace donation widget */}
            <div className="hidden md:flex md:w-auto md:flex-shrink-0 md:items-start md:justify-start">
              <iframe
                src="https://project-widget.betterplace.org/projects/170320?l=de"
                width="100%"
                height="320"
                style={{ border: 0, padding: 0, margin: 0, maxWidth: 380 }}
                title="Betterplace Spenden-Widget"
              >
                Informieren und spenden:{" "}
                <a
                  href="https://www.betterplace.org/de/projects/170320-ru-digital-de-digitaler-guide-fuer-deinen-religionsunterricht"
                  target="_blank"
                  rel="noreferrer"
                >
                  „ru-digital.de - Digitaler Guide für deinen
                  Religionsunterricht"
                </a>{" "}
                auf betterplace.org öffnen.
              </iframe>
            </div>
          </div>
        </section>

        {/* ===== Aktuelle Medien ===== */}
        <section className="mt-6">
          <h2 className="text-2xl font-bold text-textColor-primary lg:text-[40px]">
            Aktuelle Medien
          </h2>
          <p className="mt-2 max-w-3xl text-base leading-relaxed text-textColor-primary">
            Lass dich inspirieren und entdecke neue Möglichkeiten für deinen
            Religionsunterricht!
          </p>

          <div className="mt-8">
            <div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {mediaItems.map((media) => (
                  <ListItem
                    key={media.id}
                    to={mediaLink(media.id, media.name, media.slug)}
                  >
                    <UnifiedCardHome
                      name={media.name}
                      subtitle={media.authorsAsString ?? ""}
                      imageUrl={
                        media.firstPicture
                          ? getImageUrl(media.firstPicture.file.url)
                          : ""
                      }
                      layout="horizontal"
                    />
                  </ListItem>
                ))}
              </div>

              {/* CTA button */}
              <div className="mt-10 flex w-full justify-center">
                <Link
                  to="/search/main"
                  className={classNames(
                    base,
                    "ml-3 inline-flex items-center rounded-full border border-transparent bg-btnColor-secondary px-6 py-2 text-xs font-medium text-white shadow-sm hover:bg-btnColor-hoveSecondary focus:outline-none focus:ring-2 focus:ring-btnColor-secondary focus:ring-offset-2 md:px-8 md:text-sm"
                  )}
                >
                  Entdecke {mediaCount} Unterrichtsmedien
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
